import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import React, { FC, useEffect, useState } from 'react';
import { Close } from '@material-ui/icons';
import {
    CheckBlueIcon,
    ContentLoader,
    DotsIcon,
    EmptyState,
    ListTable,
    MobileCardRowsProps,
    MobileTable,
    Pagination,
    palette,
    Paper,
    SearchInput,
    SendIcon,
    SimplePopover,
    SimplePopoverPlainOption,
    Typography as T,
} from '@dev/ee-components';
import {
    AccessLevel,
    CompressionFormat,
    ContactsList,
    ExportFileFormats,
    MarketingOptions,
} from '@dev/ee-api/ee-api-models';
import { push, push as Push } from 'redux-first-history';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { parse } from 'query-string';
import { map, noop, truncate } from 'lodash-es';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';

import useMobile from '@/services/hooks/useMobile';
import {
    getListstStore,
    ListAddStart,
    ListCreateNthStart,
    ListCreateRandomStart,
    ListDeleteStart,
    ListsGetNamesStart,
    ListsLoadStart,
} from '@/store/lists/actions';
import { AccountEditMarketingOptionsStart, getAccountAccessLevel, getAccountStore } from '@/store/account/actions';
import { parseLegacyUrl } from '@/services/tools/parseLegacyUrl';
import { NewList, UrlSearchParams } from '@/store/lists/types';
import { getConfigStore } from '@/store/config/actions';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { links } from '@/services/dictionaries/links';
import { ShowDialogParams } from '@/store/dialog/types';
import { DisableDialog, HideDialog, ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { ExportContactsParams } from '@/store/exports/types';
import { ExportContactsStart } from '@/store/exports/actions';
import { useSecurity } from '@/services/hooks/useSecurity';
import { debounce } from '@/services/tools/debounce';

import { SetCampaignCreatorOpen } from '@/store/campaigns/actions';
import ListsImage from '@/services/Layout/icons/lists.svg';
import { getCampaignsStore } from '@/store/campaigns/actions';
import { CopyListOrSegment } from '../CommonActions/CopyListOrSegment';
import { DeleteList } from '../CommonActions/Delete';
import { ExportContacts } from '../CommonActions/Export';
import { RandomNthSelection } from '../CommonActions/RandomNth';
import { StartCampaignCreator } from '../../CampaignCreator/Start';
import { ContactsListsTableProps, DropdownOptions, HandleMenuClickParams, SingleListMenuProps } from './ListsInterface';

const ImageWrapper = styled.div`
    margin: 24px 48px;
    background: ${palette.indigo[1]};
    border: 1px solid ${palette.gray[3]};
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    position: relative;
    .textWrapper {
        max-width: 60%;
        padding: 24px 0px 24px 40px;
    }
    .imageWrapper {
        max-width: 40%;
    }
    .closeIcon {
        position: absolute;
        top: 10px;
        right: 10px;
        cursor: pointer;
        fill: ${palette.gray[7]};
    }
`;

const ScreenContainer = styled.div`
    input {
        padding: 0;
        border: 0; //prevent 2018 styles
    }
    & .MuiAlert-root {
        margin: 24px 0;
        color: ${palette.gray[7]};
    }
    & .MuiRadio-root {
        padding: 2px 9px;
    }
`;

const SingleMenuContainer = styled.div`
    div .icon--color {
        fill: ${palette.gray[6]};
    }
`;

const MobileTableContainer = styled.div`
    & > div {
        padding-bottom: 2px;
    }
    & .MuiContainer-root.MuiContainer-maxWidthLg {
        padding: 0 20px;
    }
`;

const CheckIconWrapper = styled.div`
    display: flex;
    justify-content: end;
`;

const TableTitleWrapper = styled.div`
    max-width: 200px;
    word-break: break-all;
`;

const SkeletonTemplate = {
    tableTitle: <ContentLoader mode="row" />,
    tracklisthistory: <ContentLoader mode="row" />,
    subscriptionmanagement: <ContentLoader mode="row" />,
};

const SingleListMenu: React.FC<SingleListMenuProps> = (props: SingleListMenuProps): JSX.Element => {
    const { list, index, perPage, activePage, search } = props;
    const account = useSelector(getAccountStore);
    const lists = useSelector(getListstStore);
    const { enableContactFeatures } = useSecurity();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const canExport = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.Export);
    const canModifyContacts = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyContacts);
    const options = [
        {
            value: 'addcontacts',
            id: 'add-contacts-option',
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: 'uploadcontacts',
            id: 'upload-contacts-option',
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: 'edit',
            id: 'edit-list-option',
            available: canModifyContacts,
        },
        {
            value: 'copy',
            id: 'copy-list-option',
            available: canModifyContacts,
        },
        {
            value: 'export',
            id: 'export-list-option',
            available: canExport,
        },
        {
            value: 'randomlist',
            id: 'create-random-list-option',
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: 'nthselectionlists',
            id: 'creat-nth-selection-list-option',
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: 'delete',
            id: 'delete-list-option',
            available: canModifyContacts,
        },
    ];
    const menuoptions = options
        .filter((option): boolean => option.available)
        .map((option): { value: string; label: string; id: string } => ({
            value: option.value,
            label: t(option.value),
            id: option.id,
        }));

    return (
        <SingleMenuContainer>
            {menuoptions.length > 0 && (
                <SimplePopover
                    id={`more-actions-row-${index}`}
                    type="icon"
                    options={menuoptions}
                    onItemClick={(e, obj: { label: string; value: string }): void => {
                        e.preventDefault();
                        const handleClickParams = {
                            option: obj.value,
                            list,
                            perPage,
                            activePage,
                            lists,
                            dispatch,
                            t,
                            search,
                        };
                        handleMenuClick(handleClickParams);
                    }}
                    placement={'bottom-end'}
                    popperProps={{
                        disablePortal: false,
                    }}
                >
                    <DotsIcon />
                </SimplePopover>
            )}
        </SingleMenuContainer>
    );
};

const skeletonRow = {
    key: 'skeleton',
    id: 'all',
    name: <Skeleton />,
    trackHistory: <Skeleton />,
    subscription: <Skeleton />,
    icon: <Skeleton />,
    menu: <Skeleton />,
};

const skeletonArray = [...Array(DEFAULT_PER_PAGE_COUNT)].fill(skeletonRow);

const DesktopTable: React.FC<ContactsListsTableProps> = (props: ContactsListsTableProps): JSX.Element => {
    const { perPage, activePage, search } = props;
    const currentAccessLevels = useSelector(getAccountAccessLevel);
    const config = useSelector(getConfigStore);
    const lists = useSelector(getListstStore);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const parseLists = (): any[] => {
        const canModifyCampaigns = currentAccessLevels.includes(AccessLevel.ModifyCampaigns);

        return lists?.collection?.map(
            (list: ContactsList, index: number): Record<string, any> => ({
                key: list.ListName,
                id: list.ListName,
                subscription: list.AllowUnsubscribe,
                trackHistory: list.TrackHistory,
                name: list.ListName,
                icon: canModifyCampaigns && (
                    <div id={`send-button-row-${index}`}>
                        <SendIcon />
                    </div>
                ),
                menu: (
                    <SingleListMenu
                        list={list}
                        index={index}
                        perPage={perPage}
                        activePage={activePage}
                        search={search}
                    />
                ),
            })
        );
    };

    const headerName = [
        { id: 'name', numeric: false, disablePadding: true, label: t('name') },
        { id: 'tracklisthistory', numeric: true, disablePadding: false, label: t('trackinghistory') },
        { id: 'subscriptionmanagement', numeric: true, disablePadding: false, label: t('subscriptionmanagement') },
        { id: 'icons', numeric: false, disablePadding: false, label: '' },
    ];

    const visibleLists = lists.collection ? parseLists() : [];

    return (
        <ListTable
            id="contacts-list-table"
            columnWidth={{
                name: '45%',
                tracklisthistory: '130px',
                subscriptionmanagement: '180px',
            }}
            stickyHeader
            headers={headerName}
            rows={lists?.collection && !lists?.isListLoading && !loading ? visibleLists : skeletonArray}
            onClickRow={(event, list): void => {
                dispatch(Push(ContactsPaths.SingleList.replace(':listname', safelyEncodeBase64Url(list.name))));
            }}
            isEmpty={visibleLists.length === 0 && !lists.isListLoading}
            onClickMenu={noop}
            onClickIcon={(event, list): void => {
                event.preventDefault();
                if (config.isStandalone) {
                    dispatch(SetCampaignCreatorOpen(true, list.name));
                    return;
                }
                const url = parseLegacyUrl(
                    ContactsPaths.CampaignCreatorTargetOld.replace(':listname', safelyEncodeBase64Url(list.name)),
                    config.isStandalone
                );
                dispatch(Push(url));
            }}
            emptyStateProps={{
                text: t('emptystate'),
            }}
        />
    );
};

const MobileTables: React.FC<ContactsListsTableProps> = (props: ContactsListsTableProps): JSX.Element => {
    const { perPage, activePage, search } = props;
    const lists = useSelector(getListstStore);
    const account = useSelector(getAccountStore);
    const config = useSelector(getConfigStore);
    const { enableContactFeatures } = useSecurity();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    if (!lists?.isListLoading && !lists?.totalCount) {
        return (
            <Paper>
                <EmptyState text={t('emptystate')} />
            </Paper>
        );
    }

    const getMobileTableHeaders = (): string[] => [t('tracklisthistory'), t('subscriptionmanagement')];

    const getMobileTableRows = (): MobileCardRowsProps[] => {
        const SkeletonRows = map([...new Array(5)], (): MobileCardRowsProps => SkeletonTemplate);
        const rows = map(lists?.collection, (list): MobileCardRowsProps => {
            const listName = truncate(list.ListName, { length: 25, omission: '...' });
            return {
                tableTitle: <TableTitleWrapper>{listName}</TableTitleWrapper>,
                tracklisthistory: list.TrackHistory ? (
                    <CheckIconWrapper>
                        <CheckBlueIcon />
                    </CheckIconWrapper>
                ) : (
                    <div />
                ),
                subscriptionmanagement: list.AllowUnsubscribe ? (
                    <CheckIconWrapper>
                        <CheckBlueIcon />
                    </CheckIconWrapper>
                ) : (
                    <div />
                ),
                rowButtonText: t('sendcampaign'),
                returnOnItemClick: list as any,
            };
        });
        return lists.isListLoading ? SkeletonRows : rows;
    };

    const getMobileDropdownTableOptions = (): SimplePopoverPlainOption[] => {
        const canExport = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.Export);
        const canModifyContacts = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyContacts);

        const options = [
            {
                label: t('viewcontacts'),
                value: DropdownOptions.ViewContacts,
                available: true,
            },
            {
                label: t('addcontacts'),
                value: DropdownOptions.Addcontacts,
                available: canModifyContacts && enableContactFeatures,
            },
            {
                label: t('export'),
                value: DropdownOptions.Export,
                available: canExport,
            },
            {
                label: t('randomlist'),
                value: DropdownOptions.Randomlist,
                available: canModifyContacts && enableContactFeatures,
            },
            {
                label: t('nthselectionlists'),
                value: DropdownOptions.Nthselectionlists,
                available: canModifyContacts && enableContactFeatures,
            },
            {
                label: t('delete'),
                value: DropdownOptions.Delete,
                available: canModifyContacts,
            },
        ];

        return options
            .filter((option): boolean => option.available)
            .map((option): { value: string; label: string } => ({
                value: option.value,
                label: t(option.value),
            }));
    };

    return (
        <MobileTableContainer>
            <MobileTable
                headers={getMobileTableHeaders()}
                rows={getMobileTableRows()}
                dropdownData={getMobileDropdownTableOptions()}
                buttonText={t('sendcampaign')}
                onClickButton={(event, list): void => {
                    event.preventDefault();
                    const url = parseLegacyUrl(
                        ContactsPaths.CampaignCreatorTargetOld.replace(
                            ':listname',
                            safelyEncodeBase64Url(list.listName as string)
                        ),
                        config.isStandalone
                    );
                    dispatch(Push(url));
                }}
                onItemClick={(e, obj: { label: string; value: string }, list): void => {
                    e.preventDefault();
                    const handleClickParams = {
                        option: obj.value,
                        list,
                        perPage,
                        activePage,
                        lists,
                        dispatch,
                        t,
                        search,
                    };
                    handleMenuClick(handleClickParams);
                }}
            />
        </MobileTableContainer>
    );
};

const WelcomeImage: React.FC = (): JSX.Element => {
    const account = useSelector(getAccountStore);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isReseller } = useSecurity();
    const handleCloseImage = (marketingOption): void => {
        const marketingOptions = [...account.GeneralInfo.MarketingOptions];
        marketingOptions.push(marketingOption);
        dispatch(AccountEditMarketingOptionsStart(marketingOptions));
    };

    return (
        <ImageWrapper>
            <div className="textWrapper">
                <T variant="h2">{t('lists')}</T>
                <T variant="body1">
                    {t('createlistdesc')}{' '}
                    {!isReseller && (
                        <a href={links.contactListsAndSegments} rel="noreferrer" target="_blank">
                            {t('learnmore')}
                        </a>
                    )}
                </T>
            </div>

            <div className="imageWrapper">
                <ListsImage />
            </div>
            <Close
                className="closeIcon"
                fontSize="large"
                onClick={(): void => {
                    handleCloseImage(MarketingOptions.ListsInfoDisplayed);
                }}
            />
        </ImageWrapper>
    );
};

const handleMenuClick = (params: HandleMenuClickParams): void => {
    const { list, lists, perPage, activePage, dispatch, t, option, search } = params;
    const name = safelyEncodeBase64Url(list.ListName);
    const handleDeleteContacts = (list): void => {
        const params: ShowDialogParams = {
            title: t('deletelist'),
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => dispatch(ListDeleteStart(list.ListName, perPage, activePage, search)),
            cancelLabel: t('cancel'),
            body: <DeleteList isList={true} />,
            id: 'delete-list-dialog',
        };
        dispatch(ShowDialog(params));
    };

    const handleAddRandomNthList = (single: boolean, selected: ContactsList): void => {
        if (lists && !lists.listNamesCollection) dispatch(ListsGetNamesStart());
        let newList = { ...lists.newList };
        const prepareParams = (list): void => {
            newList = { ...newList, ...list };
        };

        const params: ShowDialogParams = {
            title: single ? t('createrandomlist') : t('createnthselectionlists'),
            confirmLabel: t('save'),
            isHiddenManually: !single,
            onClickConfirm: (): void => {
                if (single) {
                    dispatch(ListCreateRandomStart(newList));
                    return;
                }
                dispatch(ListCreateNthStart(newList));
                dispatch(DisableDialog(true));

                const checkIfNthSelectionAreCreated = setInterval(() => {
                    if (lists?.isNthListCreated) {
                        return;
                    }
                    dispatch(HideDialog());
                    dispatch(
                        ListsLoadStart({
                            limit: perPage,
                            offset: perPage * (activePage - 1),
                        })
                    );
                    clearInterval(checkIfNthSelectionAreCreated);
                }, 1000);
            },
            cancelLabel: t('cancel'),
            body: (
                <RandomNthSelection
                    singlelist={single}
                    selected={selected}
                    newList={lists.newList}
                    onPrepareParams={prepareParams}
                />
            ),
            isDisabled: true,
        };
        dispatch(ShowDialog(params));
    };
    const handleExportContacts = (selected: ContactsList): void => {
        const exportedData: ExportContactsParams = {
            fileName: selected.ListName,
            rule: `List = '${selected.ListName}'`,
            fileFormat: ExportFileFormats.Csv,
            emails: [],
            compressionFormat: CompressionFormat.Zip,
        };
        const params: ShowDialogParams = {
            title: t('export'),
            confirmLabel: t('export'),
            onClickConfirm: (): void => dispatch(ExportContactsStart(exportedData)),
            cancelLabel: t('cancel'),
            body: <ExportContacts selected={exportedData} />,
        };
        dispatch(ShowDialog(params));
    };
    const copyList = (listData: NewList): void => {
        dispatch(UpdateDialog({ isLoading: true }));
        dispatch(ListAddStart(listData))
            .then(() => {
                dispatch(HideDialog());
            })
            .catch(() => {
                dispatch(UpdateDialog({ isLoading: false }));
            });
    };
    const handleCopyList = (list: ContactsList): void => {
        const listData = {
            ListName: `${list.ListName} ${t('copy')}`,
            Rule: `List = '${list.ListName}'`,
        };
        let newListName = listData.ListName;
        const updateSelectedName = (inputName): void => {
            newListName = inputName;
        };
        const params: ShowDialogParams = {
            title: t('copylist'),
            confirmLabel: t('copy'),
            onClickConfirm: (): void => copyList({ ...listData, ListName: newListName }),
            cancelLabel: t('cancel'),
            body: <CopyListOrSegment selected={listData} isList={true} onChange={updateSelectedName} />,
            isHiddenManually: true,
        };
        dispatch(ShowDialog(params));
    };

    switch (option) {
        case DropdownOptions.Edit:
            dispatch(Push(ContactsPaths.ListEditor.replace(':listname', name)));
            break;
        case DropdownOptions.Delete:
            handleDeleteContacts(list);
            break;
        case DropdownOptions.Copy:
            handleCopyList(list);
            break;
        case DropdownOptions.Export:
            handleExportContacts(list);
            break;
        case DropdownOptions.Randomlist:
            handleAddRandomNthList(true, list);
            break;
        case DropdownOptions.Nthselectionlists:
            handleAddRandomNthList(false, list);
            break;
        case DropdownOptions.Addcontacts:
            dispatch(Push(ContactsPaths.ListAdd.replace(':listname', safelyEncodeBase64Url(list.ListName))));
            break;
        case DropdownOptions.Uploadcontacts:
            dispatch(Push(ContactsPaths.ListUpload.replace(':listname', safelyEncodeBase64Url(list.ListName))));
            break;
        case DropdownOptions.ViewContacts:
            dispatch(Push(ContactsPaths.SingleList.replace(':listname', name)));
            break;
        default:
            break;
    }
};

interface ListSearchI {
    perPage: number;
    setActivePage: any;
    querySearch: string;
    setSearch: any;
}

const ListSearch: FC<ListSearchI> = ({ perPage, setActivePage, querySearch, setSearch }: ListSearchI): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();

    const handleSearch = (e?: any) => {
        const search = e?.currentTarget?.value || '';
        const timout = search ? 600 : 0;
        debounce(() => {
            setActivePage(1);
            setSearch(search);

            const listLoadParams = {
                limit: perPage,
                offset: 0,
                ...(!!search && { search }),
            };

            dispatch(ListsLoadStart(listLoadParams));
        }, timout);
    };

    return (
        <div style={isMobile ? { padding: '32px 0' } : { margin: '24px 48px' }}>
            <SearchInput
                id="search-for-list-input"
                label=""
                placeholder={t('searchforlists')}
                type="text"
                defaultValue={querySearch}
                onChange={handleSearch}
                onDelete={handleSearch}
            />
        </div>
    );
};

export const Lists: FC = (): JSX.Element => {
    const location = useLocation();
    const query: any = parse(location.search);
    const [activePage, setActivePage] = useState(parseInt(query.page, 10) || 1);
    const [perPage, setPerPage] = useState(parseInt(query.perPage, 10) || DEFAULT_PER_PAGE_COUNT);
    const [search, setSearch] = useState(query.search || '');
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const lists = useSelector(getListstStore);
    const config = useSelector(getConfigStore);
    const account = useSelector(getAccountStore);
    const { isCampaignCreatorOpen } = useSelector(getCampaignsStore);

    const isSavedRef = React.useRef(true);
 
    const historyBackAction = useHistoryBackAction({
        isSavedRef,
    });

    useEffect(() => {
        const query: UrlSearchParams = parse(location.search);
        const activePage = +query.page || 1;
        const perPage = +query.perPage || DEFAULT_PER_PAGE_COUNT;
        const search = query.search;
        const offset = activePage * perPage - perPage;
        dispatch(ListsLoadStart({ limit: perPage, offset, search }));
    }, []);

    const renderPagination = (): JSX.Element => {
        const pagination = (
            <Pagination
                id="lists-pagination"
                activePage={activePage}
                perPage={perPage}
                dataLength={lists.totalCount}
                handlePaginationChange={(newActivePage, newPerPage): void => {
                    const offset = newActivePage * newPerPage - newPerPage;
                    dispatch(ListsLoadStart({ limit: newPerPage, offset, search }));
                    setActivePage(newActivePage);
                    setPerPage(newPerPage);
                }}
                isRecalculatingActivePage={false}
                disableBorderTop={isMobile}
            />
        );

        return isMobile ? <Paper padding={{ top: 24, bottom: 24 }}>{pagination}</Paper> : pagination;
    };

    const showWelcomeImage = account.GeneralInfo.MarketingOptions.includes(MarketingOptions.ListsInfoDisplayed);

    return isMobile ? (
        <>
            <ListSearch perPage={perPage} setActivePage={setActivePage} querySearch={search} setSearch={setSearch} />
            <MobileTables perPage={perPage} activePage={activePage} search={search} />
            {!lists?.isListLoading && !!lists?.totalCount && renderPagination()}
        </>
    ) : (
        <ScreenContainer>
            <HistoryBackActionContext.Provider value={historyBackAction}>
                <Paper margin={{ top: config?.isStandalone ? 32 : -1 }} borderRadiusTop={!config?.isStandalone && 0}>
                    {!showWelcomeImage && <WelcomeImage />}
                    <ListSearch
                        perPage={perPage}
                        setActivePage={setActivePage}
                        querySearch={search}
                        setSearch={setSearch}
                    />
                    <DesktopTable perPage={perPage} activePage={activePage} search={search} />
                    {renderPagination()}
                </Paper>
                {isCampaignCreatorOpen && <StartCampaignCreator />}
            </HistoryBackActionContext.Provider>
        </ScreenContainer>
    );
};

export default Lists;
